/**
 * Pure obfuscation functions for Demo (Incognito) Mode.
 *
 * Key properties:
 * - Deterministic: same real value → same fake value within a session
 * - Session-scoped: seed is random on every page load, so fakes differ across sessions
 * - Display-only: API calls always use real values
 */

// Session seed — new random value on every page load (not persisted)
const SESSION_SEED = crypto.getRandomValues(new Uint32Array(2)).join('')

/** djb2-style hash returning a non-negative 32-bit integer */
function hash(s: string): number {
  let h = 5381
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) >>> 0
  }
  return h >>> 0
}

function seedHash(value: string): number {
  return hash(SESSION_SEED + value)
}

// ── Word lists for pool names ────────────────────────────────────────────────

const ADJECTIVES = [
  'Alpha', 'Amber', 'Aurora', 'Azure', 'Bright', 'Carbon', 'Cobalt',
  'Coral', 'Crystal', 'Dawn', 'Delta', 'Echo', 'Ember', 'Flux',
  'Forest', 'Frost', 'Golden', 'Granite', 'Harbor', 'Indigo',
  'Jade', 'Lunar', 'Maple', 'Mira', 'Nova', 'Onyx', 'Opal',
  'Prism', 'Quartz', 'Ridge', 'River', 'Ruby', 'Sierra', 'Solar',
  'Spark', 'Steel', 'Storm', 'Summit', 'Sunset', 'Tidal', 'Titan',
  'Tundra', 'Verdant', 'Volt', 'Zenith',
]

const NOUNS = [
  'Array', 'Basin', 'Bay', 'Beacon', 'Blaze', 'Block', 'Bridge',
  'Cache', 'Cell', 'Circuit', 'Cluster', 'Coast', 'Depot', 'Drift',
  'Field', 'Fleet', 'Flow', 'Forge', 'Gate', 'Grid', 'Harbor',
  'Hub', 'Isle', 'Ledge', 'Link', 'Loop', 'Matrix', 'Mesh',
  'Nexus', 'Node', 'Orbit', 'Patch', 'Peak', 'Pillar', 'Relay',
  'Reserve', 'Ring', 'Ridge', 'Sector', 'Shard', 'Stack', 'Station',
  'Tower', 'Trunk', 'Vault', 'Wave', 'Zone',
]

// ── Word lists for company names ─────────────────────────────────────────────

const COMPANY_PREFIXES = [
  'Altis', 'Apex', 'Arcen', 'Ardent', 'Arvio', 'Aston', 'Axion',
  'Baltic', 'Beacon', 'Boreal', 'Brynn', 'Calix', 'Cerno', 'Ceto',
  'Claros', 'Crest', 'Crux', 'Dalis', 'Deltan', 'Denvo', 'Doran',
  'Dusk', 'Elara', 'Elsin', 'Enten', 'Erion', 'Eston', 'Ethos',
  'Farro', 'Fenix', 'Feron', 'Flint', 'Forta', 'Galen', 'Glint',
  'Halcyon', 'Halis', 'Haven', 'Helio', 'Helion', 'Holst', 'Hydron',
  'Ikona', 'Illex', 'Inara', 'Inox', 'Ionis', 'Irex', 'Ixon',
  'Joren', 'Kalm', 'Keston', 'Kindra', 'Klaro', 'Koren', 'Kyros',
  'Lanto', 'Laren', 'Larkon', 'Latom', 'Lavon', 'Lenis', 'Lexon',
  'Lumex', 'Lunor', 'Luxen', 'Lynton', 'Maren', 'Maxon', 'Melior',
  'Meron', 'Miron', 'Miston', 'Modix', 'Moray', 'Narden', 'Naros',
  'Necto', 'Nelos', 'Neston', 'Nexar', 'Noric', 'Noris', 'Norvin',
  'Novex', 'Noxen', 'Nuris', 'Nyron', 'Oaken', 'Oblix', 'Octan',
  'Omnis', 'Onira', 'Orcan', 'Orcen', 'Ordon', 'Orion', 'Orvex',
  'Palen', 'Panox', 'Parix', 'Parkon', 'Paston', 'Paxon', 'Pelion',
  'Renox', 'Rexton', 'Rilon', 'Rivon', 'Rolan', 'Romax', 'Ronex',
  'Salix', 'Salon', 'Saron', 'Saton', 'Selion', 'Senox', 'Seron',
  'Silex', 'Siron', 'Solion', 'Solix', 'Soran', 'Sorin', 'Sovex',
  'Talion', 'Talon', 'Tanox', 'Taren', 'Tarex', 'Taron', 'Telion',
  'Teron', 'Timax', 'Tiron', 'Titan', 'Tomax', 'Toran', 'Torin',
  'Ulton', 'Unex', 'Uniox', 'Unison', 'Upton', 'Urion', 'Urnex',
  'Valix', 'Valor', 'Valon', 'Varos', 'Varon', 'Vaston', 'Velion',
  'Veron', 'Vestan', 'Vexon', 'Vilon', 'Viron', 'Volton', 'Voran',
  'Warden', 'Welion', 'Welton', 'Weston', 'Wexon', 'Whiton', 'Wilton',
  'Xanon', 'Xaren', 'Xeron', 'Xiron', 'Xolan', 'Xoron', 'Xuron',
  'Yaren', 'Yaron', 'Yolon', 'Yoran', 'Yoton', 'Yulon', 'Yuron',
  'Zaren', 'Zaron', 'Zelon', 'Zenon', 'Zeron', 'Zilon', 'Zoron',
]

const COMPANY_SUFFIXES = [
  'AG', 'BV', 'Co', 'Corp', 'Dynamics', 'Energy', 'Flex',
  'GmbH', 'Grid', 'Group', 'Holdings', 'Industries', 'Innovations',
  'Laboratories', 'Networks', 'Partners', 'Power', 'Services',
  'Solutions', 'Systems', 'Technologies', 'Ventures',
]

// ── Obfuscation helpers ──────────────────────────────────────────────────────

/** Returns a fake hex segment of given length, deterministically derived from a seed number */
function fakeHex(seed: number, len: number): string {
  let out = ''
  let n = seed
  for (let i = 0; i < len; i++) {
    n = ((n * 1664525 + 1013904223) >>> 0)
    out += (n & 0xf).toString(16)
  }
  return out
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Returns a fake identifier derived deterministically from `real`.
 *
 * - If `real` is a standard UUID (36 chars), returns a properly formatted fake UUID.
 * - Otherwise, preserves the exact same length and separator positions so layout
 *   never shifts — hex chars are replaced with fake hex, separators are kept.
 */
export function obfuscateUUID(real: string): string {
  const h = seedHash(real)

  if (UUID_PATTERN.test(real)) {
    // Standard UUID: produce a proper RFC 4122 v4-shaped fake
    const a = fakeHex(h, 8)
    const b = fakeHex(h ^ 0xdeadbeef, 4)
    const c = '4' + fakeHex(h ^ 0x12345678, 3)
    const variant = (8 + (h & 3)).toString(16)
    const d = variant + fakeHex(h ^ 0xabcdef01, 3)
    const e = fakeHex(h ^ 0xfeedface, 12)
    return `${a}-${b}-${c}-${d}-${e}`
  }

  // Non-UUID: preserve exact length and separator positions to avoid layout shifts
  let out = ''
  let n = h
  for (let i = 0; i < real.length; i++) {
    const ch = real[i]
    if (ch === '-' || ch === '_' || ch === '.') {
      out += ch
    } else {
      n = ((n * 1664525 + 1013904223) >>> 0)
      out += (n & 0xf).toString(16)
    }
  }
  return out
}

/**
 * Returns an energy-themed fake pool name derived deterministically from `real`.
 * Example: "Amber Grid", "Sunset Fleet"
 */
export function obfuscatePoolName(real: string): string {
  const h = seedHash(real)
  const adj = ADJECTIVES[h % ADJECTIVES.length]
  const noun = NOUNS[(h >>> 8) % NOUNS.length]
  return `${adj} ${noun}`
}

/**
 * Returns a fake company name derived deterministically from `real`.
 * Example: "Helion Energy", "Solion GmbH"
 */
export function obfuscateAccountName(real: string): string {
  const h = seedHash(real)
  const prefix = COMPANY_PREFIXES[h % COMPANY_PREFIXES.length]
  const suffix = COMPANY_SUFFIXES[(h >>> 10) % COMPANY_SUFFIXES.length]
  return `${prefix} ${suffix}`
}

/**
 * Returns generic tag replacements: keys become "tag-N", values become "value-A".
 */
export function obfuscateTags(real: Record<string, string>): Record<string, string> {
  const entries = Object.entries(real)
  const result: Record<string, string> = {}
  entries.forEach(([k, v], i) => {
    const fakeKey = `tag-${i + 1}`
    const h = seedHash(k + v)
    const letter = String.fromCharCode(65 + (h % 26))
    result[fakeKey] = `value-${letter}`
  })
  return result
}

/** Returns a fake email deterministically derived from `real`. */
export function obfuscateEmail(real: string): string {
  const h = seedHash(real)
  const user = fakeHex(h, 6)
  const domains = ['example.com', 'demo.io', 'gridx-demo.de', 'testenv.net']
  const domain = domains[h % domains.length]
  return `${user}@${domain}`
}

/** Returns a fake display name deterministically derived from `real`. */
export function obfuscateUserName(real: string): string {
  const h = seedHash(real)
  const firstNames = ['Alex', 'Jamie', 'Morgan', 'Sam', 'Taylor', 'Jordan', 'Robin', 'Casey']
  const lastNames = ['Demo', 'Test', 'Grid', 'Energy', 'Power', 'Flex', 'Volt', 'Amp']
  return `${firstNames[h % firstNames.length]} ${lastNames[(h >>> 4) % lastNames.length]}`
}
